import { useEffect, useState } from "react";
import { BookOpen, Cloud, CloudOff, EyeOff, Flame, LoaderCircle, Orbit, ShieldCheck, UserRound } from "lucide-react";
import { GrimoireModule } from "./components/GrimoireModule";
import { ShrineModule } from "./components/ShrineModule";
import { SigilModule } from "./components/SigilModule";
import { StealthMode } from "./components/StealthMode";
import { useAuth } from "./hooks/useAuth";
import { useSanctumSync } from "./hooks/useSanctumSync";

type Tab = "shrine" | "sigil" | "grimoire";

export default function App() {
  const [tab, setTab] = useState<Tab>("shrine");
  const [stealth, setStealth] = useState(false);
  const auth = useAuth();
  const sync = useSanctumSync(auth);
  useEffect(() => {
    const panic = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setStealth((value) => !value); }
    };
    window.addEventListener("keydown", panic, { capture: true });
    return () => window.removeEventListener("keydown", panic, { capture: true });
  }, []);
  return <>
    <div className={`min-h-screen ${stealth ? "pointer-events-none invisible h-screen overflow-hidden" : ""}`} aria-hidden={stealth} inert={stealth}>
    <header className="site-header">
      <a href="/sanctum/" className="brand"><span className="brand-seal">☾</span><span><b>THE QUIET ARCHIVE</b><small>devotion · will · remembrance</small></span></a>
      <nav className="primary-nav" aria-label="Archive modules">
        <NavButton active={tab === "shrine"} onClick={() => setTab("shrine")} icon={<Flame size={16}/>}>Shrine</NavButton>
        <NavButton active={tab === "sigil"} onClick={() => setTab("sigil")} icon={<Orbit size={16}/>}>Sigil studio</NavButton>
        <NavButton active={tab === "grimoire"} onClick={() => setTab("grimoire")} icon={<BookOpen size={16}/>}>Grimoire</NavButton>
      </nav>
      <div className="header-actions">
        <SyncBadge status={auth.loading ? "loading" : sync}/>
        {auth.authenticated ? <span className="account-name"><UserRound size={14}/>{auth.user?.name}</span> : <a className="account-link" href={auth.signInUrl}><ShieldCheck size={14}/> Sign in to unlock</a>}
        <button className="panic-button" onClick={() => setStealth(true)} title="Panic screen (Esc)"><EyeOff size={15}/><span>ESC</span></button>
      </div>
    </header>

    <main className="site-main">
      <div className="page-intro">
        <div><p className="eyebrow">{tab === "shrine" ? "A private devotional room" : tab === "sigil" ? "Chaos magick instrument" : "Account-bound memory"}</p><h1>{tab === "shrine" ? "An altar kept in real time." : tab === "sigil" ? "Reduce the sentence. Keep the current." : "What has been kept—and released."}</h1></div>
        <p>{tab === "shrine" ? "Tend offerings, keep mandas, and shape a respectful prayer without surrendering your own judgment." : tab === "sigil" ? "A procedural vector studio grounded in Spare-style letter reduction, planetary mapping, and deliberate banishing." : "Account records are encrypted at rest and opened only through your authenticated session."}</p>
      </div>
      {tab === "shrine" ? <ShrineModule/> : tab === "sigil" ? <SigilModule/> : <GrimoireModule/>}
    </main>

    <footer className="site-footer"><span>Encrypted at rest. Unsigned work remains in memory only; companion requests are always explicit.</span><span>Esc opens the neutral workspace immediately.</span></footer>
    </div>
    {stealth && <StealthMode onExit={() => setStealth(false)} />}
  </>;
}

function NavButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return <button className={active ? "active" : ""} onClick={onClick}>{icon}{children}</button>;
}

function SyncBadge({ status }: { status: string }) {
  const Icon = status === "locked" ? ShieldCheck : status === "offline" || status === "local" ? CloudOff : status === "loading" || status === "saving" ? LoaderCircle : Cloud;
  const label = status === "synced" ? "Encrypted" : status === "saving" ? "Encrypting" : status === "loading" ? "Unlocking" : status === "locked" ? "Archive locked" : status === "offline" ? "Offline" : "Memory only";
  return <span className={`sync-badge status-${status}`}><Icon size={12} className={status === "loading" || status === "saving" ? "animate-spin" : ""}/>{label}</span>;
}
